import { useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { t } = useTranslation();

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((open) => !open);
  const handleDropdown = (idx) => {
    setOpenDropdown(openDropdown === idx ? null : idx);
  };

  const menuItems = [
    {
      title: t('nav.home'),
      link: "/",
    },
    { title: t('nav.faq'), link: "/faq" },
    {
      title: "Dashboard",
      link: "/dashboard",
    },
  ];

  const renderMenu = (items, level = 1, mobile = false, parentIdx = null) => (
    <ul
      className={`space-y-1 ${level > 1 ? "pl-4 border-l border-gray-200" : ""}`}
      role={level === 1 ? "menubar" : "menu"}
    >
      {items.map((item, idx) => {
        const isDropdownOpen = mobile && openDropdown === `${parentIdx}-${idx}`;
        const isPagesDropdown = item.title === "Pages";
        return (
          <li
            key={idx}
            className={mobile ? "relative" : "group relative"}
            role="none"
          >
            {item.link ? (
              <Link
                to={item.link}
                className="block py-2 px-3 rounded transition hover:bg-gray-100 focus:bg-gray-200 focus:outline-none"
                role="menuitem"
                tabIndex={0}
                onClick={mobile ? closeMenu : undefined}
              >
                {item.title}
              </Link>
            ) : (
              <button
                className={`block py-2 px-3 w-full text-center rounded transition hover:bg-gray-100 focus:bg-gray-200 focus:outline-none flex items-center justify-between`}
                onClick={mobile ? () => handleDropdown(`${parentIdx}-${idx}`) : undefined}
                type="button"
                aria-haspopup={!!item.dropdown}
                aria-expanded={mobile ? isDropdownOpen : undefined}
              >
                <span>{item.title}</span>
                {item.dropdown && (
                  <span className={`ml-2 text-xs transition-transform duration-200 ${isDropdownOpen ? "rotate-90" : "rotate-0"}`}>
                    ▶
                  </span>
                )}
              </button>
            )}

            {item.dropdown && (
              mobile ? (
                <div
                  className={`overflow-hidden transition-all duration-300 ${isDropdownOpen ? "max-h-96 opacity-100 mt-1" : "max-h-0 opacity-0"}`}
                >
                  {renderMenu(item.dropdown, level + 1, mobile, `${parentIdx}-${idx}`)}
                </div>
              ) : (
                <div className="absolute top-full left-0 bg-white shadow-lg z-20 hidden group-hover:block min-w-[180px] p-4 rounded transition-all duration-200 border border-gray-100">
                  {renderMenu(item.dropdown, level + 1)}
                </div>
              )
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <header className="sticky top-0 z-50 bg-white shadow transition-all duration-200">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 min-w-[120px]">
          <img src="/assets/images/homepage/stocknow_logo.png" alt="Logo" className="h-12 w-auto object-contain" />
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex space-x-7 items-center" role="navigation" aria-label="Main navigation">
          {menuItems.map((item, idx) => (
            <div key={idx} className="relative group">
              <span className="text-gray-800 font-medium cursor-pointer hover:text-primary-600 transition">
                {item.link ? (
                  <Link to={item.link} className="px-1 py-1 rounded focus:outline-none focus:bg-gray-100 transition">
                    {item.title}
                  </Link>
                ) : (
                  <span tabIndex={0} className="focus:outline-none">{item.title}</span>
                )}
              </span>

              {item.dropdown && (
                <div className="absolute top-full left-0 bg-white shadow-lg z-20 hidden group-hover:block min-w-[180px] p-4 rounded transition-all duration-200">
                  {renderMenu(item.dropdown)}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div>
          <LanguageSwitcher />
        </div>

        {/* Mobile toggle button */}
        <button
          className="md:hidden text-2xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 rounded p-2 transition"
          onClick={toggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu - from top, not sidebar */}
      {menuOpen && (
        <div className="md:hidden absolute left-0 top-full w-full bg-white shadow-lg border-t z-40 animate-fadeInDown">
          <div className="px-4 py-4">
            {renderMenu(menuItems, 1, true, "root")}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
